import type { TFunction } from "i18next";
import {
  AccessEvents,
  AuthEvents,
  InviteEvents,
  MarketplaceAdminEvents,
  MarketplaceEvents,
  OpportunityEvents,
  PersonaEvents,
  PersonaOpportunityEvents,
  PreflightEvents,
  SyncEvents,
  SystemEvents,
  TemplateEvents,
  TenantEvents,
  UserEvents,
  WorkspaceEvents,
  type EventName,
  type MarketplaceAdminEventName,
} from "@nexus/contracts";

const CATALOG_FALLBACK = {
  [OpportunityEvents.GENERATE_NEW_SET_REQUESTED]: "Generate new set requested",
  [OpportunityEvents.GENERATE_REQUESTED]: "Generate requested",
  [OpportunityEvents.STORYBOARD_GENERATION_REQUESTED]: "Storyboard generation requested",
  [OpportunityEvents.AI_EDIT_SCRIPT_REQUESTED]: "AI edit script requested",
  [OpportunityEvents.SUGGEST_SCRIPT_REQUESTED]: "Suggest script requested",
  [OpportunityEvents.RESOLVE_REFERENCE_LINKS_REQUESTED]:
    "Resolve reference links requested",
  [OpportunityEvents.GENERATED]: "Generated",
  [OpportunityEvents.SCORED]: "Scored",
  [OpportunityEvents.APPROVED]: "Approved",
  [OpportunityEvents.REJECTED]: "Rejected",
  [OpportunityEvents.BULK_REJECT_REQUESTED]: "Bulk reject requested",
  [OpportunityEvents.EXPORT_REQUESTED]: "Export requested",
  [UserEvents.REGISTERED]: "User registered",
  [UserEvents.UPDATED]: "User updated",
  [UserEvents.DEACTIVATED]: "User deactivated",
  [UserEvents.REACTIVATED]: "User reactivated",
  [UserEvents.ACCESS_GRANTED]: "Access granted",
  [UserEvents.ACCESS_REVOKED]: "Access revoked",
  [UserEvents.ACCESS_BULK_GRANTED]: "Access bulk granted",
  [UserEvents.ACCESS_BULK_REVOKED]: "Access bulk revoked",
  [UserEvents.ACCESS_ALL_REVOKED]: "All access revoked",
  [UserEvents.ACCESS_REPLACED]: "Access replaced",
  [TenantEvents.PROVISIONED]: "Tenant provisioned",
  [TenantEvents.UPDATED]: "Tenant updated",
  [TenantEvents.DEACTIVATED]: "Tenant deactivated",
  [TenantEvents.REACTIVATED]: "Tenant reactivated",
  [TenantEvents.IMPORT_SETTINGS]: "Import tenant settings",
  [AccessEvents.CREATED]: "Access created",
  [AccessEvents.BULK_CREATED]: "Access bulk created",
  [AccessEvents.UPDATED]: "Access updated",
  [AccessEvents.DELETED]: "Access deleted",
  [AccessEvents.RESTORED]: "Access restored",
  [AccessEvents.PERMISSIONS_UPDATED]: "Access permissions updated",
  [AccessEvents.HOLDERS_REFRESH_REQUESTED]: "Access holders refresh requested",
  [TemplateEvents.CREATED]: "Template created",
  [TemplateEvents.UPDATED]: "Template updated",
  [TemplateEvents.STATUS_CHANGED]: "Template status changed",
  [TemplateEvents.GRANTED]: "Template granted",
  [TemplateEvents.REVOKED]: "Template revoked",
  [TemplateEvents.APPLIED_TO_ORG]: "Template applied to organization",
  [TemplateEvents.REMOVED_FROM_ORG]: "Template removed from organization",
  [InviteEvents.CREATED]: "Invite created",
  [InviteEvents.REGENERATED]: "Invite regenerated",
  [InviteEvents.RESENT]: "Invite resent",
  [InviteEvents.REVOKED]: "Invite revoked",
  [SystemEvents.HEALTH_PING]: "Health ping",
  [SystemEvents.CHANGELOG_GENERATE_REQUESTED]: "Changelog generate requested",
  [SystemEvents.CHANGELOG_DELIVER_REQUESTED]: "Changelog deliver requested",
  [SystemEvents.CHANGELOG_DIGEST_GENERATE_REQUESTED]:
    "Changelog digest generate requested",
  [SystemEvents.CHANGELOG_DIGEST_DELIVER_REQUESTED]:
    "Changelog digest deliver requested",
  [SystemEvents.CHANGELOG_SYNC_REQUESTED]: "Changelog sync requested",
  [SystemEvents.ADMIN_OPS_SWEEP_REQUESTED]: "Admin ops sweep requested",
  [AuthEvents.LOGIN_SUCCEEDED]: "Login succeeded",
  [AuthEvents.LOGOUT]: "Logout",
  [AuthEvents.MFA_ENABLED]: "MFA enabled",
  [AuthEvents.MFA_DISABLED]: "MFA disabled",
  [SyncEvents.ALL_REQUESTED]: "Sync all requested",
  [SyncEvents.TENANT_SYNC_REQUESTED]: "Tenant sync requested",
  [SyncEvents.TENANT_KPI_PRECACHE_REQUESTED]: "Tenant KPI precache requested",
  [SyncEvents.BRAND_BRAIN_FETCH_REQUESTED]: "Brand Brain fetch requested",
  [SyncEvents.TENANT_ONBOARDING_FETCH_REQUESTED]: "Tenant onboarding fetch requested",
  [SyncEvents.TENANT_ONBOARDING_CREATE_REQUESTED]: "Tenant onboarding create requested",
  [SyncEvents.TENANT_ONBOARDING_EDIT_REQUESTED]: "Tenant onboarding edit requested",
  [SyncEvents.CUSTOMER_PARTITION_CHECK_REQUESTED]: "Customer partition check requested",
  [SyncEvents.TENANT_ONBOARDING_SYNC_REQUESTED]: "Onboarding import from BigQuery requested",
  [PreflightEvents.REPORT_REQUESTED]: "Report requested",
  [PreflightEvents.SCORE_ASSET]: "Score asset",
  [PreflightEvents.UPLOAD_CONFIRMED]: "Upload confirmed",
  [PreflightEvents.SCRIPT_REVIEW_REQUESTED]: "Script review requested",
  [PreflightEvents.SCRIPT_REVIEW_COMMENT_REJECTED]: "Script review comment rejected",
  [PreflightEvents.SCRIPT_MATCH_REQUESTED]: "Script match requested",
  [PreflightEvents.PARTNER_VOICEOVER_CHECK_REQUESTED]: "Partner voiceover check requested",
  [WorkspaceEvents.DATA_REFRESH_REQUESTED]: "Data refresh requested",
  [WorkspaceEvents.DATA_REFRESHED]: "Data refreshed",
  [WorkspaceEvents.OPTIONS_REQUESTED]: "Options requested",
  [MarketplaceEvents.ORDER_SENT]: "Order sent",
  [MarketplaceEvents.SCRIPT_PREFLIGHT_SUBMITTED]: "Script preflight submitted",
  [MarketplaceEvents.SCRIPT_REVISION_REQUESTED]: "Script revision requested",
  [MarketplaceEvents.SCRIPT_COMMENT_ADDED]: "Script comment added",
  [MarketplaceEvents.SCRIPT_SUBMITTED_FOR_FINAL_APPROVAL]:
    "Script submitted for final approval",
  [MarketplaceEvents.VENDOR_CONNECTED]: "Partner connected",
  [MarketplaceEvents.VENDOR_TERMS_UPDATED]: "Partner terms updated",
  [MarketplaceEvents.OVERDUE_ORDERS_SWEEP_REQUESTED]:
    "Overdue orders sweep requested",
  [PersonaEvents.DISCOVER_REQUESTED]: "Discover personas requested",
  [PersonaEvents.FEEDBACK_READY]: "Persona feedback ready",
  [PersonaEvents.ONBOARDING_COMPLETED]: "Persona onboarding completed",
  [PersonaEvents.CORE_SYNC]: "Core persona sync",
  [PersonaEvents.INDUSTRY_SYNC]: "Industry persona sync",
  [PersonaEvents.AVATAR_GENERATION_REQUESTED]: "Avatar generation requested",
  [PersonaOpportunityEvents.GENERATE_SET_REQUESTED]: "Generate persona set requested",
  [PersonaOpportunityEvents.GENERATE_SCRIPTS_REQUESTED]:
    "Generate persona scripts requested",
  [PersonaOpportunityEvents.GENERATE_HOOK_VARIATIONS_REQUESTED]:
    "Generate hook variations requested",
  [PersonaOpportunityEvents.AI_EDIT_SCRIPTS_REQUESTED]:
    "AI edit persona scripts requested",
  [PersonaOpportunityEvents.AI_EDIT_BRIEFS_REQUESTED]:
    "AI edit persona briefs requested",
  [PersonaOpportunityEvents.STORYBOARD_GENERATION_REQUESTED]:
    "Persona storyboard generation requested",
  [PersonaOpportunityEvents.SUGGEST_SCRIPTS_REQUESTED]:
    "Suggest persona scripts requested",
  [PersonaOpportunityEvents.IMPORT_SCRIPT_FROM_TEXT_REQUESTED]:
    "Import script from text requested",
  [PersonaOpportunityEvents.IMPORT_BRIEF_FROM_TEXT_REQUESTED]:
    "Import brief from text requested",
  [PersonaOpportunityEvents.RESOLVE_REFERENCE_LINKS_REQUESTED]:
    "Resolve persona reference links requested",
  [PersonaOpportunityEvents.MIGRATE_LEGACY_TENANT_REQUESTED]:
    "Migrate legacy opportunities to hub requested",
} as const satisfies Record<EventName, string>;

/**
 * Observability-only names written to event_jobs (not in `EventName`).
 * The Events drawer still has to label them.
 */
const RECORDED_FALLBACK = {
  [MarketplaceAdminEvents.ORDER_CREATED]: "Order created",
  [MarketplaceAdminEvents.ORDER_SENT]: "Order sent",
  [MarketplaceAdminEvents.ORDER_STATUS_CHANGED]: "Order status changed",
  [MarketplaceAdminEvents.ORDER_ETA_UPDATED]: "ETA updated",
  [MarketplaceAdminEvents.ORDER_MARK_READY]: "Marked ready",
  [MarketplaceAdminEvents.ORDER_REVISION_REQUESTED]: "Revision requested",
  [MarketplaceAdminEvents.ORDER_FINAL_APPROVAL]: "Final approval",
  [MarketplaceAdminEvents.ORDER_HANDOFF_SNAPSHOT_CREATED]:
    "Handoff snapshot created",
  [MarketplaceAdminEvents.PUBLISH_ATTEMPTED]: "Publish attempted",
  [MarketplaceAdminEvents.PUBLISH_SUCCEEDED]: "Publish succeeded",
  [MarketplaceAdminEvents.PUBLISH_FAILED]: "Publish failed",
  [MarketplaceAdminEvents.PUBLISH_TIMEOUT]: "Publish timed out",
  [MarketplaceAdminEvents.PUBLISH_RETRIED]: "Publish retried",
  [MarketplaceAdminEvents.PUBLISH_BEST_EFFORT_DROPPED]:
    "Publish dropped (best effort)",
  [MarketplaceAdminEvents.NOTIFY_RECEIVED]: "Notification received",
  [MarketplaceAdminEvents.NOTIFY_RECIPIENTS_RESOLVED]: "Recipients resolved",
  [MarketplaceAdminEvents.NOTIFY_ZERO_RECIPIENTS_SKIPPED]:
    "Zero recipients — skipped",
  [MarketplaceAdminEvents.NOTIFY_DELIVERY_FAILED]:
    "Notification delivery failed",
  [MarketplaceAdminEvents.EMAIL_SENT]: "Email sent",
  [MarketplaceAdminEvents.EMAIL_FAILED]: "Email failed",
  [MarketplaceAdminEvents.EMAIL_SKIPPED]: "Email skipped",
  [MarketplaceAdminEvents.PREFLIGHT_ATTEMPTED]: "Preflight attempted",
  [MarketplaceAdminEvents.PREFLIGHT_SUBMITTED]: "Preflight submitted",
  [MarketplaceAdminEvents.PREFLIGHT_PUBLISH_LEG]: "Preflight publish leg",
  [MarketplaceAdminEvents.PREFLIGHT_ROLLBACK]: "Preflight rollback",
  [MarketplaceAdminEvents.PREFLIGHT_ACK_PERSIST_FAILED]:
    "Preflight acknowledgement persistence failed",
  [MarketplaceAdminEvents.PREFLIGHT_RESUBMIT]: "Preflight resubmit",
  [MarketplaceAdminEvents.PREFLIGHT_STORYBOARD_URL_ISSUED]:
    "Storyboard URL issued",
  [MarketplaceAdminEvents.SCRIPT_REVIEW_CREATED]: "Script review created",
  [MarketplaceAdminEvents.SCRIPT_REVIEW_FAILED]: "Script review failed",
  [MarketplaceAdminEvents.SCRIPT_REVIEW_SHARE_MINTED]: "Share link minted",
  [MarketplaceAdminEvents.SCRIPT_REVIEW_SHARE_REVOKED]: "Share link revoked",
  [MarketplaceAdminEvents.SCRIPT_REVIEW_CLIENT_VIEWER_GET]:
    "Client viewer opened review",
  [MarketplaceAdminEvents.SCRIPT_REVIEW_NOTE_POSTED]: "Review note posted",
  [MarketplaceAdminEvents.IMPORT_BATCH_PROVISIONED]: "Import batch provisioned",
  [MarketplaceAdminEvents.IMPORT_COMMIT_STARTED]: "Import commit started",
  [MarketplaceAdminEvents.IMPORT_COMMIT_FINISHED]: "Import commit finished",
  [MarketplaceAdminEvents.IMPORT_RELEASE_SENT]: "Import row released",
  [MarketplaceAdminEvents.IMPORT_RELEASE_SKIPPED]: "Import row skipped",
  [MarketplaceAdminEvents.IMPORT_RELEASE_RETRIED]: "Import row retried",
  [MarketplaceAdminEvents.IMPORT_FROZEN_LINK_RELEASED]:
    "Frozen import link released",
  [MarketplaceAdminEvents.VENDOR_CONNECTED]: "Partner connected",
  [MarketplaceAdminEvents.VENDOR_TERMS_UPDATED]: "Partner terms updated",
  [MarketplaceAdminEvents.VENDOR_CATALOG_CHANGED]: "Partner catalog changed",
  [MarketplaceAdminEvents.VENDOR_RECIPIENTS_CHANGED]:
    "Notification recipients changed",
  [MarketplaceAdminEvents.AUTHZ_DENIED]: "Authorization denied",
} as const satisfies Record<MarketplaceAdminEventName, string>;

const FALLBACK: Record<string, string> = {
  ...CATALOG_FALLBACK,
  ...RECORDED_FALLBACK,
};

export function eventTypeLabel(t: TFunction, eventType: string): string {
  const fallback = Object.hasOwn(FALLBACK, eventType)
    ? FALLBACK[eventType] ?? eventType
    : eventType;
  return t(`admin.events.types.${eventType}`, fallback);
}
